<template lang="pug">
  .scheduleAdd
    //- Barra de navegación
    van-nav-bar(
      :title="$t('scene.scene')"
      left-arrow
      @click-left="close"
      @click-right="saveScene")
      van-icon(name="save" slot="right")

    //- Barra de notificaciones de error
    .schedule-notify-error(v-if="errorDevicesEmpty || errors.has('name')")
      div(v-if="errors.has('name')") {{ $t("scene.errorForm") }}
      div(v-if="errorDevicesEmpty") {{ $t('scene.errorDevicesEmpty') }}

    .main(
      :class="{'hasButton': scene, 'error-notify': errorDevicesEmpty || errors.has('name')}")

      //- Name input
      .van-cell-group__title
        | {{ $t("scene.name") }}
        .float-right {{ $t("scene.icon") }}

      van-cell-group.schedule__add__name
        van-field(
          type="text"
          name="name"
          v-model="name"
          v-validate="{ required: true, max:15, min:1, regex: /^.{1,15}$/ }"
          :data-vv-as="$t('scene.name')"
          :class="{'error': errors.has('name') }"
          :placeholder="$t('scene.scene')")

        div.schedule__add__icon(@click="showManageIcons")
          van-icon(:name="`scene-${icon}`")

      .auth__error.padding-halve-top.padding-halve-bottom(v-show="errors.has('name')")
        | {{ errors.first('name') }}

      //- Select settings
      .van-cell-group__title {{ $t("scene.settings") }}


      //- Settings - Power
      .schedule__power
        button.powerOff(
          :class="{'powerOff--select': power}"
          @click="setOff")
        button.powerOn(
          :class="{'powerOn--select': power}"
          @click="setOn")


      .schedule__settings(:class="{hide: !power}")

        //- Settings - Mode
        .schedule__mode
          .schedule__mode__title {{ GetModeTitle(mode) }}
          .unit__modes
            van-icon(
              v-for="(item, key) in modes"
              :name="GetModeIcon(item)"
              :key="key"
              :class="{select: item === mode}"
              @click="setMode(item)")


        //- Settings - Temp
        .schedule__temp(
          :class="{hide: mode === MODES.FAN || mode === MODES.DRY}"
          @click="openSetDefaultTemp")
          | {{ setpoint }}
          span {{ installation.units | UnitFilter}}


        //- Settings - Fan
        .schedule__fan(
          :class="{hide: mode === MODES.DRY || !speedsFan}")
          .schedule__fan__title {{ getFan }}
          .unit__fans
            van-icon(
              v-for="(fan, key) in speedsFan"
              :name="'fan' + fan"
              :class="{select: parseInt(speed_state, 10) === parseInt(key, 10)}"
              :key="key"
              :alt="fan + '-' + key + ':' + speed_state"
              @click="setFan(key)")


      //- Select units
      .van-cell-group__title {{ $t("scene.units") }}
      .auth__error.padding-halve-top.padding-halve-bottom(v-if="errorDevicesEmpty")
        | {{ $t('scene.errorDevicesEmpty') }}


      .schedule__units
        .units__device__bar(
          v-for="(device, mac) in devices"
          :class="{disabled: !device.mode}"
          :key="mac"
          @click="(device.mode) ? setDevice(mac) : null")
          .units__device__name
            van-icon.media__figure(:name="GetDeviceIcon(device)")
            | {{ device.name}}
          .units__device__action
            .checkbox
              label(:class="{'select': checkDevice(mac) && device.mode}") &nbsp;

    .button-bottom-group
      .button-bottom.text-danger(
        v-if="scene"
        @click="confirmDeleteScene") {{ $t('scene.delete') }}

    //- Popup Select Icon
    van-popup.van-popup--full.bg-white(
      v-model="openManageIcons"
      position="right"
      :overlay="false")

      ManageIcons(
        v-if="openManageIcons"
        :icon="icon"
        @setIcon="setIcon"
        @close="hideManageIcons")
</template>

<script>
import { sanitizeScene } from '@/models/scenes.model';
import ManageIcons from '@/components/Scenes/ManageIcons.vue';
import SceneService from '@/services/scene.service';
import GetDeviceIcon from '@/mixins/GetDeviceIcon.mixin';
import GetModeTitle from '@/mixins/GetModeTitle.mixin';
import GetModeIcon from '@/mixins/GetModeIcon.mixin';
import { REGEX, MODES } from '@/app.config';
import { UnitFilter } from '@/filters/Temp.filter';
import { getErrorId } from '@/services/error.service';
import { mapGetters, mapActions } from 'vuex';

export default {
  components: { ManageIcons },
  mixins: [GetDeviceIcon, GetModeTitle, GetModeIcon],
  filters: { UnitFilter },
  data() {
    return {
      name: '',
      icon: 1,
      power: true,
      mode: 1,
      modes: [1, 2, 3, 4, 5],
      setpoint: null,
      speed_state: 0,
      speedsFan: null,
      selectedDevices: [],
      acsRange: null,
      autoRange: null,
      coolRange: null,
      hotRange: null,
      nameRegex: REGEX.SCENES_NAMES,
      isLoading: false,
      sceneError: null,
      errorDevicesEmpty: false,
      openManageIcons: false,
      MODES,
    };
  },
  props: {
    installationID: {
      type: String,
      required: true,
    },

    scene: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      getInstallation: 'unitsModule/getInstallation',
      getDevices: 'unitsModule/getDevices',
    }),

    /**
     * Devuelve los datos de la instalación almacenados en el Store
     *
     * @return {Installation} - Datos del dispositivo
     */
    installation() {
      return this.getInstallation(this.installationID);
    },

    /**
     * Devuelve el listado de los dispositivos del tipo indicado en la instalación
     *
     * @return {Array[Device]} - Listado de dispositivos
     */
    devices() {
      return this.getDevices(this.installationID);
    },

    /**
     * Devuelve la velocidad seleccionada en texto
     */
    getFan() {
      if (!this.speedsFan) return null;

      let fanSpeed = this.speedsFan[this.speed_state];

      if (!fanSpeed) {
        fanSpeed = parseInt(Object.getOwnPropertyNames(this.speedsFan)[0], 10);
      }

      if (parseInt(fanSpeed, 10) === 0) return this.$i18n.t('scene.fanAuto');
      return `${this.$i18n.t('scene.fanLevel')} ${fanSpeed}`;
    },

    /**
     * Devuelve el rango de temperaturas según el modo seleccionado
     */
    setpointRange() {
      switch (this.mode) {
        case 1:
          return this.autoRange;
        case 2:
          return this.coolRange;
        case 3:
          return this.hotRange;
        default:
          return [];
      }
    },
  },

  methods: {
    ...mapActions({
      saveSceneStore: 'unitsModule/saveScene',
      updateSceneStore: 'unitsModule/updateScene',
      deleteSceneStore: 'unitsModule/deleteScene',
    }),

    /**
     * Muestra el modal para seleccionar el icono
     */
    showManageIcons() {
      this.openManageIcons = true;
    },

    /**
     * Oculta el modal para seleccionar el icono
     */
    hideManageIcons() {
      this.openManageIcons = false;
    },

    /**
     * Guarda el icono seleccionado y cierra el modal
     */
    setIcon(icon) {
      const device = {
        acs_available: this.acsAvailable,
        icon,
      };
      this.icon = icon;
      this.iconName = this.GetDeviceIcon(device);
      this.hideManageIcons();
    },

    /**
     * Muestra el selctor de temperatura
     */
    openSetDefaultTemp() {
      const configTemp = {
        title: this.$i18n.t('scene.selectTemp'),
        items: [[this.setpointRange]],
        positiveButtonText: this.$i18n.t('scene.done'),
        negativeButtonText: this.$i18n.t('scene.cancel'),
        theme: 'dark',
        defaultItems: {
          0: `${this.setpoint}`,
        },
      };

      window.SelectorCordovaPlugin.showSelector(configTemp, result => {
        this.setpoint = parseInt(result[0].description, 10);
      });
    },

    /**
     * escena de apagado
     */
    setOff() {
      this.power = false;
    },

    /**
     * escena de encendido
     */
    setOn() {
      this.power = true;
    },

    /**
     * Cambia el modo de funcionamiento y actualiza la temperatura
     */
    setMode(mode) {
      this.mode = mode;
      this.checkSetPoint();
    },

    /**
     * Cambia la velocidad del ventilador
     */
    setFan(fan) {
      this.speed_state = parseInt(fan, 10);
    },

    setDevice(mac) {
      const index = this.selectedDevices.findIndex(macItem => macItem === mac);

      if (index >= 0) {
        this.selectedDevices.splice(index, 1);
      } else {
        const device = Object.keys(this.devices).find(macItem => macItem === mac);
        this.selectedDevices.push(device);
      }

      this.getLimits();
    },

    /**
     * Según los dispositivos seleccionados define:
     *  - El rango de temperatura
     *  - Modos disponibles
     *  - Velocidades disponibles
     */
    getLimits() {
      /**
       * Defino los valores máximos posibles
       * Actualmente no se pueden programar clima con agua solo con aire
       * por lo que no tengo que diferenciarlos
       */
      const unit = this.installation.units;
      let modes = [1, 2, 3, 4, 5];
      let speedsFan = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      let maxAutoTemp = unit === 0 ? 35 : 87;
      let minAutoTemp = unit === 0 ? 15 : 58;
      let maxCoolTemp = unit === 0 ? 35 : 87;
      let minCoolTemp = unit === 0 ? 15 : 58;
      let maxHotTemp = unit === 0 ? 32 : 87;
      let minHotTemp = unit === 0 ? 15 : 58;
      const maxACSTemp = unit === 0 ? 65 : 140;
      const minACSTemp = unit === 0 ? 5 : 77;
      let allUnitsTai = true;

      /**
       * Obtengo el listado de dispositivos
       * si hay algún dispositivo seleccionado, utilizo estos,
       * sino, utilizo todos los dispositivos de la instalación
       */
      const listDevicesToCompare =
        this.selectedDevices.length !== 0 ? this.selectedDevices : this.getListMacs(this.devices);

      /**
       * Recorro todos los dispositivos y me voy quedando con los valores más restrictivos
       */
      listDevicesToCompare.forEach(mac => {
        const device = this.devices[mac];

        if (!device.acs_available) allUnitsTai = false;
        if (device.mode_available) modes = this.concatArrays(modes, device.mode_available);
        if (device.speed_available) {
          speedsFan = this.concatArrays(speedsFan, device.speed_available);
        }
        if (device.range_sp_auto_air_max) {
          maxAutoTemp = Math.min(maxAutoTemp, device.range_sp_auto_air_max);
        }
        if (device.range_sp_auto_air_min) {
          minAutoTemp = Math.max(minAutoTemp, device.range_sp_auto_air_min);
        }
        if (device.range_sp_cool_air_max) {
          maxCoolTemp = Math.min(maxCoolTemp, device.range_sp_cool_air_max);
        }
        if (device.range_sp_cool_air_min) {
          minCoolTemp = Math.max(minCoolTemp, device.range_sp_cool_air_min);
        }
        if (device.range_sp_hot_air_max) {
          maxHotTemp = Math.min(maxHotTemp, device.range_sp_hot_air_max);
        }
        if (device.range_sp_hot_air_min) {
          minHotTemp = Math.max(minHotTemp, device.range_sp_hot_air_min);
        }
      });

      /**
       * Compruebo si todas las unidades tienen disponible ACS
       * para definir los saltos de temperatura
       */
      const stepTemp = listDevicesToCompare.every(mac => {
        return this.devices[mac].acs_available;
      })
        ? 0.5
        : 1;

      /**
       * Si hay alguna unidad que tiene disponible ACS pero no todas,
       * puede que los valores límite sean con medio grado,
       * en este caso, redondeo los valores a un valor entero
       */
      if (stepTemp === 1) {
        maxAutoTemp = Math.floor(maxAutoTemp);
        minAutoTemp = Math.floor(minAutoTemp);
        maxCoolTemp = Math.floor(maxCoolTemp);
        minCoolTemp = Math.floor(minCoolTemp);
        maxHotTemp = Math.floor(maxHotTemp);
        minHotTemp = Math.floor(minHotTemp);
      }

      /**
       * Si se diera el caso de que ningún modo o velocidad coincida en todos los dispositivos
       * muestro las opciones por defecto
       * Si todos los dipositivos seleccionados son tai, no tendrán velocidad
       */
      this.modes = modes.length ? modes : [1, 2, 3, 4, 5];

      if (allUnitsTai) {
        this.speedsFan = this.getSpeedsFan();
      } else {
        this.speedsFan = speedsFan.length
          ? this.getSpeedsFan(speedsFan)
          : this.getSpeedsFan([2, 4, 6]);
      }

      /**
       * Cuando edito una escena, puede darse el caso que el modo o la velocidad seleccionada
       * ya no esté disponible, si hemos añadido o eliminado dispositivos,
       * en este caso actualizo el modo o la velocidad con el primer valor disponible
       */
      if (!this.modes.some(mode => mode === this.mode)) {
        this.mode = this.modes[0]; // eslint-disable-line
      }

      if (this.speedsFan && (!this.speed_state || !this.speedsFan[this.speed_state])) {
        this.speed_state = parseInt(Object.getOwnPropertyNames(this.speedsFan)[0], 10); // eslint-disable-line
      }

      /**
       * Genero un array con los valores de temperatura disponibles
       * según el modo para utilizarlos en el selector de temperatura
       */
      this.acsRange = this.generateArrayNumbers(minACSTemp, maxACSTemp, 0.5);
      this.autoRange = this.generateArrayNumbers(minAutoTemp, maxAutoTemp, stepTemp);
      this.coolRange = this.generateArrayNumbers(minCoolTemp, maxCoolTemp, stepTemp);
      this.hotRange = this.generateArrayNumbers(minHotTemp, maxHotTemp, stepTemp);

      /**
       * Actualiza el valor de la temperatura para que se ajuste a los valores límites y el modo
       */
      this.checkSetPoint();
    },

    /**
     * Define el valor de la temperatura en base al modo seleccionado y los rangos de temperatura
     */
    checkSetPoint() {
      // Obtengo los rangos en función del modo
      let minTemp = 0;
      let maxTemp = 0;

      switch (this.mode) {
        case MODES.AUTO:
          minTemp = parseInt(this.autoRange[0].description, 10); // eslint-disable-line
          maxTemp = parseInt(this.autoRange[this.autoRange.length - 1].description, 10);
          break;
        case MODES.COOLING:
          minTemp = parseInt(this.coolRange[0].description, 10); // eslint-disable-line
          maxTemp = parseInt(this.coolRange[this.coolRange.length - 1].description, 10);
          break;
        case MODES.HEATING:
          minTemp = parseInt(this.hotRange[0].description, 10); // eslint-disable-line
          maxTemp = parseInt(this.hotRange[this.hotRange.length - 1].description, 10);
          break;
        default:
          break;
      }

      // Defino el valor de temperatura medio dentro del rango
      if (!minTemp && !maxTemp) {
        this.setpoint = '--';
      } else if (this.setpoint < minTemp || this.setpoint > maxTemp || this.setpoint === '--') {
        this.setpoint = parseInt((maxTemp - minTemp) / 2, 10) + minTemp;
      }
    },

    /**
     * Comprueba si una mac se encuentra dentro del listado de mac seleccionadas
     */
    checkDevice(mac) {
      return this.selectedDevices.some(macItem => {
        return macItem === mac;
      });
    },

    /**
     * Devuelve un listado de macs de un listado de dispositivos
     */
    getListMacs(devices) {
      const listDevices = [];
      Object.keys(devices).forEach(mac => listDevices.push(mac));
      return listDevices;
    },

    /**
     * Toma dos arrays y devuelve un nuevo array con los elementos comunes
     */
    concatArrays(array1, array2) {
      return array1.filter(item => {
        const exist = array2.findIndex(item2 => item2 === item);
        return exist >= 0;
      });
    },

    /**
     * Devuelve un objeto con las velocidas disponibles parseadas
     * para que sean una secuencia lógica de números enteros.
     *
     * Las velocidades disponibles pueden no tener un orden lógico (empezar por 1, o incrementos de 1)
     * en este caso genero un objeto con su valor real (fan) y un valor parseado (key)
     *
     * El modo Auto tiene el valor 0.
     *
     * Ejemplo:
     *  - velocidades disponibles => [0,2,3,5,7]
     *  - velocidades parseadas => { 0:0, 2:1, 3:2, 5:3, 7:4 }
     */
    getSpeedsFan(speeds) {
      if (!speeds) return null;

      const auto = speeds.indexOf(0);
      const speedsObject = {};

      speeds.forEach((fan, key) => {
        key = auto > -1 ? key : key + 1;
        speedsObject[fan] = key;
      });

      return speedsObject;
    },

    /**
     * Devuelve un array de números entre dos números pasados
     */
    generateArrayNumbers(from, to, step) {
      const list = [];
      for (let i = from; i <= to; i += step) {
        list.push({ description: `${i}` });
      }

      return list;
    },

    /**
     * Salva los datos de una escena
     *
     * Comprueba si los datos del formulario son válidos
     * Comprueba si se trata de una escena nueva o de una edición
     */
    saveScene() {
      this.errorDevicesEmpty = !this.selectedDevices.length;

      this.$validator.validateAll().then(result => {
        if (result && !this.errorDevicesEmpty) {
          // Preparo los datos
          const scene = {
            devices: this.selectedDevices,
            name: this.name,
            icon: this.icon,
            params: {},
          };

          scene.params.power = this.power;

          if (this.power) {
            scene.params.mode = this.mode;

            switch (this.mode) {
              case MODES.AUTO:
                scene.params.setpoint_air_auto = this.setpoint;
                break;
              case MODES.COOLING:
                scene.params.setpoint_air_cool = this.setpoint;
                break;
              case MODES.HEATING:
                scene.params.setpoint_air_heat = this.setpoint;
                break;
              default:
                break;
            }

            if (this.mode !== MODES.DRY) {
              scene.params.speed_state = this.speed_state;
            }
          }

          const data = {
            installationID: this.installationID,
            sceneID: this.scene ? this.scene.id : '',
            scene,
          };

          if (this.scene) {
            this.editScene(data);
          } else {
            this.addScene(data);
          }
        }
      });
    },

    /**
     * Crea una nueva escena
     */
    async addScene(data) {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: this.$i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.sceneError = null;

        // Realizo la petición
        const response = await SceneService.addScene(data);

        // Almaceno los datos en el store
        // Primero le añado el id que le ha asignado a la programción
        data.scene._id = response;
        const saveData = {
          installationID: data.installationID,
          sceneID: response,
          data: sanitizeScene(data.scene),
        };
        this.saveSceneStore(saveData);

        this.$toast.clear();
        this.$toast.success({ mask: true, message: this.$i18n.t('scene.saveSuccess') });
        this.close();
      } catch (error) {
        // Obtengo el error
        this.sceneError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({ mask: true, message: this.$i18n.t('general.errors.default.title') });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Edita una escena
     */
    async editScene(data) {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: this.$i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.sceneError = null;

        // Realizo la petición
        await SceneService.editScene(data);

        // Almaceno los datos en el store
        const editData = {
          installationID: data.installationID,
          sceneID: data.sceneID,
          data: sanitizeScene(data.scene),
        };
        this.updateSceneStore(editData);

        this.$toast.clear();
        this.$toast.success({ mask: true, message: this.$i18n.t('scene.editSuccess') });
        this.close();
      } catch (error) {
        // Obtengo el error
        this.sceneError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({ mask: true, message: this.$i18n.t('general.errors.default.title') });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Confirma si realmente quiere eliminar la escena
     */
    confirmDeleteScene() {
      this.$dialog
        .confirm({
          title: this.$i18n.t('scene.deleteConfirm.title'),
          message: this.$i18n.t('scene.deleteConfirm.message'),
        })
        .then(() => {
          this.deleteScene();
        });
    },

    /**
     * Elimina una escena
     */
    async deleteScene() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: this.$i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.sceneError = null;

        const data = {
          installationID: this.installationID,
          sceneID: this.scene.id,
        };

        // Realizo la petición
        await SceneService.deleteScene(data);

        // Modifico el store para eliminar la escena
        this.deleteSceneStore(data);

        this.$toast.clear();
        this.$toast.success({ mask: true, message: this.$i18n.t('scene.deleteSuccess') });
        this.close();
      } catch (error) {
        // Obtengo el error
        this.sceneError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({ mask: true, message: this.$i18n.t('general.errors.default.title') });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Cierra este componente
     */
    close() {
      this.$emit('close');
    },
  },

  created() {
    /**
     * Si estamos editando un usuario, asigno los valores
     */
    if (this.scene) {
      this.icon = this.scene.icon;
      this.name = this.scene.name;
      this.power = this.scene.power;
      this.mode = this.scene.mode || 1;
      this.setpoint = this.scene.setpoint || null;
      this.speed_state = this.scene.speed_state || 0;

      // TODO: Convertir el array de unidades en array de objetos device
      if (!this.scene.devices.length) {
        this.selectedDevices = Object.keys(this.devices);
      } else {
        this.selectedDevices = this.scene.devices;
      }
    }

    this.getLimits();
  },
};
</script>
